import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Trash2, FileText } from "lucide-react";

interface DocumentData {
  title?: string;
  fileName?: string;
  fileType?: string;
  fileUrl?: string;
  imagePreview?: string;
  source?: string;
  content?: string;
  size?: string;
  patientId?: string;
  description?: string;
  status?: number;
  contentType?: string;
  type?: number;
  consultationId?: string;
  documentId?: string | null;
  [key: string]: any;
}

interface DocumentItem {
  id?: string;
  sender?: string;
  message?: string;
  actionId?: number;
  details?: {
    payload?: string;
    actionName?: string;
    actionNumber?: string;
    isPerformed?: boolean;
    [key: string]: any;
  };
  isDraft?: boolean;
  displayTimestamp?: string;
  [key: string]: any;
}

const getFileExtension = (fileName: string, contentType?: string): string => {
  // Try to get extension from fileName first
  if (fileName) {
    const parts = fileName.split(".");
    if (parts.length > 1) {
      const ext = parts.pop()?.toLowerCase();
      if (ext && ext.length <= 5) {
        // Reasonable extension length
        return `.${ext}`;
      }
    }
  }

  // Fallback to contentType
  if (contentType) {
    if (contentType.includes("pdf")) return ".pdf";
    if (contentType.includes("text/plain")) return ".txt";
    if (contentType.includes("image/jpeg")) return ".jpg";
    if (contentType.includes("image/png")) return ".png";
    if (contentType.includes("image/gif")) return ".gif";
    if (contentType.includes("image/webp")) return ".webp";
    if (contentType.includes("msword")) return ".doc";
    if (contentType.includes("wordprocessingml")) return ".docx";
    if (contentType.includes("spreadsheet")) return ".xlsx";
    if (contentType.includes("presentation")) return ".pptx";
  }

  return "";
};

export const DocumentsItems = ({ items, isDarkMode, handleDocumentClick }) => {
  // Sort items by timestamp (oldest first)
  const sortedItems = [...items].sort((a: DocumentItem, b: DocumentItem) => {
    const dateA = new Date(a.displayTimestamp || 0).getTime();
    const dateB = new Date(b.displayTimestamp || 0).getTime();
    return dateA - dateB;
  });

  return (
    <div className="px-3 pb-3 mt-3 space-y-2">
      {sortedItems.map((action: DocumentItem, index: number) => {
        let documentData: DocumentData = {};

        try {
          if (
            action.details?.payload &&
            typeof action.details.payload === "string"
          ) {
            const parsed = JSON.parse(action.details.payload);
            // Handle nested payload structure
            documentData = parsed.payload || parsed;
          } else {
            // If payload is already an object
            documentData = action.details || {};
          }
        } catch (e) {
          console.warn("Failed to parse document payload", e);
          documentData = action.details || {};
        }

        const fileName =
          documentData.title ||
          documentData.fileName ||
          documentData.libraryDocTitle ||
          "Document";
        const fileNameLower = (
          documentData.fileName ||
          fileName ||
          ""
        ).toLowerCase();

        const fileExtension = getFileExtension(
          fileName,
          documentData.contentType,
        );

        const isPDF =
          fileExtension === ".pdf" ||
          (documentData.fileType || "").includes("pdf");
        const isImage = fileNameLower.match(/\.(jpg|jpeg|png|gif|webp)$/);
        const fileType =
          documentData.fileType ||
          (isPDF ? "application/pdf" : isImage ? "image" : "other");

        const isLibraryDocument =
          action.details?.actionName === "LibraryDocument" ||
          documentData.libraryDocId;
        const documentTypeText = isLibraryDocument
          ? "Library Document"
          : "Uploaded file";

        return (
          <div
            key={action.id || index}
            className="group flex items-center gap-3 p-2.5"
            onClick={() => handleDocumentClick(documentData)}
          >
            <div
              className={`flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${
                isDarkMode ? "bg-gray-700" : "bg-gray-100"
              }`}
            >
              <FileText
                className={`w-4 h-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <p
                  className={`text-sm font-medium truncate ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                >
                  <span>
                    {fileName}
                    {fileExtension && fileExtension}
                  </span>
                </p>
              </div>
              <p
                className={`text-xs truncate ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
              >
                {documentTypeText}
              </p>
            </div>
            <div
              className="flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  {/* <button
                    className={`p-1 rounded transition-colors ${
                      isDarkMode
                        ? "hover:bg-red-900/20 text-red-400 hover:text-red-300"
                        : "hover:bg-red-50 text-red-500 hover:text-red-700"
                    }`}
                    title="Delete document"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button> */}
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Document</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to remove this document? This cannot
                      be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      //   onClick={() => onDelete(action.id)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        );
      })}
    </div>
  );
};
